// src/Hooks/useImportExcelBatchSave.js

import { db, storage } from '../Firebase/firebase';
import { getApp } from 'firebase/app';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
  getBlob,
} from 'firebase/storage';
import {
  collection,
  writeBatch,
  doc,
  query,
  where,
  getDocs,
  deleteDoc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { SOUND_WAVE_ICON_SVG_STRING } from '../assets/icons/CustomIconsAdd3';

const normalizeString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

const getNormalizedFileMatch = (filesArray, targetString) => {
  if (!targetString || !Array.isArray(filesArray)) return null;
  const cleanTarget = normalizeString(targetString);
  return filesArray.find((f) => {
    if (!f || typeof f.name !== 'string') return false;

    let baseName = f.name.replace(/\.[^/.]+$/, '');
    baseName = baseName.replace(/(?:[-_ ]?\(\d+\)|[-_]\d+)$/, '');
    return (
      normalizeString(baseName) === cleanTarget || normalizeString(f.name) === cleanTarget
    );
  });
};

const yieldToMain = () => new Promise((resolve) => setTimeout(resolve, 15));

const forcePaint = () =>
  new Promise((resolve) => {
    if (document.hidden) {
      setTimeout(resolve, 0);
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 0);
        });
      });
    }
  });

const ensureValidBlob = async (fileObj) => {
  if (!fileObj) return fileObj;

  if (fileObj.preview && fileObj.preview.startsWith('blob:')) {
    try {
      const res = await fetch(fileObj.preview);
      return await res.blob();
    } catch (e) {
      console.warn('Failed to fetch blob from local preview URL', e);
    }
  }

  if (!(fileObj instanceof Blob) && fileObj.preview) {
    try {
      const res = await fetch(fileObj.preview);
      return await res.blob();
    } catch (e) {
      console.warn('Failed to fetch blob from remote preview', e);
    }
  }

  return fileObj;
};

export const useImportExcelBatchSave = (stateStore) => {
  const { state, setters, refs } = stateStore;

  const executeSave = async (skipValidation = false) => {
    const {
      parsedData,
      selectedRowIds,
      successfullyImportedIds,
      rowMappings,
      customRows,
      rowSubMappings,
      rowStaticValues,
      markerIcons,
      makeUppercase,
      categories,
      regions,
      currentFilename,
      columns,
      selectedMenuId,
      stagedFiles,
      pendingDuplicateDeletions,
      pendingReinstateBatchIds,
      pendingDocumentDeletions,
    } = state;

    try {
      refs.cancelProcessRef.current = false;
      setters.setIsCancelling(false);

      let rowsToProcess = [];
      if (selectedRowIds.length > 0) {
        rowsToProcess = parsedData.filter(
          (row) =>
            selectedRowIds.includes(row.id) && !successfullyImportedIds.includes(row.id),
        );
      } else {
        rowsToProcess = parsedData.filter(
          (row) => !successfullyImportedIds.includes(row.id),
        );
      }

      if (rowsToProcess.length === 0) {
        setters.setAlertState({
          open: true,
          message: 'No un-imported data selected.',
          severity: 'warning',
        });
        return;
      }

      const formatText = (text) => {
        if (makeUppercase && typeof text === 'string' && text) return text.toUpperCase();
        return text;
      };

      const getMappedValue = (row, target, subTarget = null) => {
        const normTarget = normalizeString(target);
        const normSubTarget = subTarget ? normalizeString(subTarget) : null;

        if (row._localMappings) {
          const localOverrideColName = Object.keys(row._localMappings).find((col) => {
            const mapping = row._localMappings[col];
            if (!mapping) return false;
            if (normalizeString(mapping.assignedTo) !== normTarget) return false;
            if (normSubTarget && normalizeString(mapping.selection) !== normSubTarget)
              return false;
            return true;
          });

          if (localOverrideColName) {
            const mapping = row._localMappings[localOverrideColName];
            if (mapping.staticValue !== null && mapping.staticValue !== undefined)
              return String(mapping.staticValue);

            const actualKey = Object.keys(row).find(
              (k) => normalizeString(k) === normalizeString(localOverrideColName),
            );
            return actualKey && row[actualKey] !== undefined && row[actualKey] !== ''
              ? String(row[actualKey])
              : null;
          }
        }

        const colKey = Object.keys(rowMappings).find((key) => {
          if (normalizeString(rowMappings[key]) !== normTarget) return false;
          if (normSubTarget && normalizeString(rowSubMappings[key]) !== normSubTarget)
            return false;
          return true;
        });

        if (!colKey) return null;
        if (rowStaticValues[colKey]) return String(rowStaticValues[colKey]);

        let colName = colKey;
        const customRowObj = customRows.find((cr) => cr.id === colKey);
        if (customRowObj && customRowObj.name !== '-') {
          colName = customRowObj.name;
        } else if (colKey.startsWith('prop_')) {
          colName = colKey.substring(5);
        } else {
          return null;
        }

        const actualRowKey = Object.keys(row).find(
          (k) => normalizeString(k) === normalizeString(colName),
        );
        return actualRowKey && row[actualRowKey] !== undefined && row[actualRowKey] !== ''
          ? String(row[actualRowKey])
          : null;
      };

      const getMappedOrFallbackValue = (
        row,
        target,
        subTarget = null,
        fallbackColName = null,
      ) => {
        // If a template shares the same column name for Title and Hover Text (or Photos and Hover Photo),
        // prefer the dedicated fallback column if it exists on the row and has data.
        if (fallbackColName && (target === 'HOVER TEXT' || target === 'HOVER PHOTO')) {
          const explicitKey = Object.keys(row).find(
            (k) => normalizeString(k) === normalizeString(fallbackColName),
          );
          if (explicitKey && row[explicitKey] !== undefined && row[explicitKey] !== '') {
            return String(row[explicitKey]);
          }
        }

        let val = getMappedValue(row, target, subTarget);
        if (val !== null && val !== undefined && val !== '') return val;

        if (fallbackColName) {
          const actualKey = Object.keys(row).find(
            (k) => normalizeString(k) === normalizeString(fallbackColName),
          );
          if (actualKey && row[actualKey] !== undefined && row[actualKey] !== '') {
            return String(row[actualKey]);
          }
        }
        return null;
      };

      let invalidCoordsCount = 0;
      let invalidPropsCount = 0;
      const problematicProperties = new Set();
      const validRowsToProcess = [];

      const uniqueMappedCols = [];
      const seenColNorms = new Set();

      customRows
        .filter((r) => rowMappings[r.id] && r.name !== '-')
        .forEach((r) => {
          const norm = normalizeString(r.name);
          if (!seenColNorms.has(norm)) {
            seenColNorms.add(norm);
            uniqueMappedCols.push(r.name);
          }
        });

      const mappedTemplateCols = uniqueMappedCols;

      const getColName = (key) => {
        if (!key) return null;
        const cRow = customRows?.find((r) => r.id === key);
        if (cRow && cRow.name !== '-') return cRow.name;
        if (key.startsWith('prop_')) return key.substring(5);
        return null;
      };

      const titleColKey = Object.keys(rowMappings).find(
        (k) => rowMappings[k] === 'INFO' && rowSubMappings[k] === 'TITLE',
      );
      const titleColName = getColName(titleColKey);

      const positionColKey = Object.keys(rowMappings).find(
        (k) => rowMappings[k] === 'POSITION (LAT, LNG)',
      );
      const positionColName = getColName(positionColKey);

      const templatePhotoColKey = Object.keys(rowMappings).find(
        (k) => rowMappings[k] === 'PHOTOS',
      );
      const templatePhotoColName = getColName(templatePhotoColKey) || 'Photos';

      const iconColKey = Object.keys(rowMappings).find((k) => rowMappings[k] === 'ICON');
      const iconColName = getColName(iconColKey);

      if (!skipValidation) {
        for (let i = 0; i < rowsToProcess.length; i++) {
          if (i % 25 === 0) {
            await yieldToMain();
          }

          if (refs.cancelProcessRef.current) break;

          const row = rowsToProcess[i];
          let lat = 0;
          let lng = 0;

          // PRIORITY 1: Explicitly assigned coordinates (e.g. from the geocoder in ExcelDataView)
          const explicitLat =
            row['lat'] ??
            row['Lat'] ??
            row['LAT'] ??
            row['latitude'] ??
            row['Latitude'] ??
            row['LATITUDE'];
          const explicitLng =
            row['lng'] ??
            row['Lng'] ??
            row['LNG'] ??
            row['longitude'] ??
            row['Longitude'] ??
            row['LONGITUDE'];

          if (
            explicitLat !== undefined &&
            explicitLng !== undefined &&
            explicitLat !== '' &&
            explicitLng !== ''
          ) {
            const pLat = parseFloat(String(explicitLat).trim());
            const pLng = parseFloat(String(explicitLng).trim());
            if (!isNaN(pLat) && !isNaN(pLng)) {
              lat = pLat;
              lng = pLng;
            }
          }

          // PRIORITY 2: Mapped string extraction via agnostic regex
          if (lat === 0 && lng === 0) {
            const posString = getMappedOrFallbackValue(
              row,
              'POSITION (LAT, LNG)',
              null,
              'POSITION (LAT, LNG)',
            );
            if (posString) {
              const matches = posString.match(/-?\d+(\.\d+)?/g);
              if (matches && matches.length >= 2) {
                const pLat = parseFloat(matches[0]);
                const pLng = parseFloat(matches[1]);
                if (!isNaN(pLat) && !isNaN(pLng)) {
                  lat = pLat;
                  lng = pLng;
                }
              }
            }
          }

          if (lat === 0 && lng === 0) {
            invalidCoordsCount++;
          } else {
            validRowsToProcess.push(row);

            for (const colName of mappedTemplateCols) {
              if (
                titleColName &&
                normalizeString(colName) === normalizeString(titleColName)
              )
                continue;
              if (
                positionColName &&
                normalizeString(colName) === normalizeString(positionColName)
              )
                continue;

              const isPhotoColumn =
                normalizeString(colName) === normalizeString(templatePhotoColName);
              const isIconCol =
                iconColName && normalizeString(colName) === normalizeString(iconColName);

              const colKey = Object.keys(rowMappings).find(
                (k) => normalizeString(getColName(k)) === normalizeString(colName),
              );
              const hasStaticValue = colKey ? !!rowStaticValues[colKey] : false;

              const isDescCol =
                colKey && normalizeString(rowMappings[colKey]) === 'description';

              const actualRowKey = Object.keys(row).find(
                (k) => normalizeString(k) === normalizeString(colName),
              );
              const cellValue = actualRowKey ? row[actualRowKey] : undefined;

              const cellHasData =
                cellValue !== undefined &&
                cellValue !== null &&
                String(cellValue).trim() !== '' &&
                String(cellValue).trim() !== '<p></p>';

              const hasDescInPhotos =
                isDescCol &&
                row.matchedFiles &&
                row.matchedFiles.some(
                  (f) =>
                    f.description &&
                    String(f.description).trim() !== '' &&
                    String(f.description).trim() !== '<p></p>',
                );

              const headerExistsInExcel = columns
                .map((c) => normalizeString(c))
                .includes(normalizeString(colName));

              const manualKey = Object.keys(row._manuallyAssigned || {}).find(
                (k) => normalizeString(k) === normalizeString(colName),
              );
              const isManuallyAssigned = !!(
                manualKey && row._manuallyAssigned[manualKey]
              );

              let isInvalidMatch = false;

              if (hasStaticValue) {
                isInvalidMatch = false;
              } else if (isPhotoColumn) {
                if (row.photoMatch !== 'MATCH') isInvalidMatch = true;
              } else if (isIconCol) {
                isInvalidMatch = false;
              } else if (isManuallyAssigned) {
                isInvalidMatch = false;
              } else if (isDescCol && (cellHasData || hasDescInPhotos)) {
                isInvalidMatch = false;
              } else if (headerExistsInExcel && cellHasData) {
                if (colKey && rowSubMappings[colKey] === 'REGION') {
                  const isMatch = regions.some(
                    (r) =>
                      r.name && normalizeString(r.name) === normalizeString(cellValue),
                  );
                  if (!isMatch) isInvalidMatch = true;
                } else if (colKey && rowSubMappings[colKey] === 'CATEGORY') {
                  const isMatch = categories.some(
                    (c) =>
                      c.name && normalizeString(c.name) === normalizeString(cellValue),
                  );
                  if (!isMatch) isInvalidMatch = true;
                }
              } else {
                isInvalidMatch = true;
              }

              if (isInvalidMatch) {
                invalidPropsCount++;
                problematicProperties.add(colName);
              }
            }
          }
        }

        if (invalidCoordsCount > 0 || invalidPropsCount > 0) {
          let msgParts = [];

          if (invalidPropsCount > 0) {
            msgParts.push(
              `There are ${invalidPropsCount} occurrences of missing or incorrect mapping which will be missed on import.`,
            );

            const uniqueProps = [];
            const seenNormalized = new Set();

            problematicProperties.forEach((p) => {
              const displayVal = formatText(p);
              const normVal = normalizeString(p);
              if (!seenNormalized.has(normVal)) {
                seenNormalized.add(normVal);
                uniqueProps.push(displayVal);
              }
            });

            uniqueProps.forEach((p) => msgParts.push(`• ${p}`));
          }

          let secondPart = [];
          if (invalidCoordsCount > 0) {
            secondPart.push(
              `${invalidCoordsCount} data rows have invalid lat/lng co-ordinates and will not be imported.`,
            );
          }
          secondPart.push('Do you want to proceed?');

          if (secondPart.length > 0) {
            msgParts.push(secondPart.join(' '));
          }

          setters.setIsProcessing(false);
          setters.setUploadProgress({ current: 0, total: 0 });

          setters.setConfirmDialog({
            open: true,
            type: 'PROCEED_WITH_INVALID',
            message: (
              <div style={{ textAlign: 'left' }}>
                {msgParts.map((part, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom:
                        index === msgParts.length - 1
                          ? 0
                          : part.startsWith('•')
                            ? '4px'
                            : '16px',
                      marginLeft: part.startsWith('•') ? '16px' : '0',
                      color: part.startsWith('•') ? '#555' : 'inherit',
                    }}>
                    {part}
                  </div>
                ))}
              </div>
            ),
            confirmText: 'OK',
            cancelText: 'Cancel',
            severity: 'warning',
          });

          return;
        }
      }

      if (skipValidation && validRowsToProcess.length === 0) {
        for (let i = 0; i < rowsToProcess.length; i++) {
          const row = rowsToProcess[i];
          let lat = 0;
          let lng = 0;

          // PRIORITY 1: Explicitly assigned coordinates (e.g. from the geocoder in ExcelDataView)
          const explicitLat =
            row['lat'] ??
            row['Lat'] ??
            row['LAT'] ??
            row['latitude'] ??
            row['Latitude'] ??
            row['LATITUDE'];
          const explicitLng =
            row['lng'] ??
            row['Lng'] ??
            row['LNG'] ??
            row['longitude'] ??
            row['Longitude'] ??
            row['LONGITUDE'];

          if (
            explicitLat !== undefined &&
            explicitLng !== undefined &&
            explicitLat !== '' &&
            explicitLng !== ''
          ) {
            const pLat = parseFloat(String(explicitLat).trim());
            const pLng = parseFloat(String(explicitLng).trim());
            if (!isNaN(pLat) && !isNaN(pLng)) {
              lat = pLat;
              lng = pLng;
            }
          }

          // PRIORITY 2: Mapped string extraction via agnostic regex
          if (lat === 0 && lng === 0) {
            const posString = getMappedOrFallbackValue(
              row,
              'POSITION (LAT, LNG)',
              null,
              'POSITION (LAT, LNG)',
            );
            if (posString) {
              const matches = posString.match(/-?\d+(\.\d+)?/g);
              if (matches && matches.length >= 2) {
                const pLat = parseFloat(matches[0]);
                const pLng = parseFloat(matches[1]);
                if (!isNaN(pLat) && !isNaN(pLng)) {
                  lat = pLat;
                  lng = pLng;
                }
              }
            }
          }

          if (lat !== 0 || lng !== 0) {
            validRowsToProcess.push(row);
          }
        }
      }

      rowsToProcess = validRowsToProcess;

      if (refs.cancelProcessRef.current) {
        setters.setIsProcessing(false);
        setters.setIsCancelling(false);
        setters.setUploadProgress({ current: 0, total: 0 });
        return;
      }

      // If any reinstated row has a _restoredHoverUrl that wasn't yet converted to a File blob,
      // try fetching its blob BEFORE pendingReinstateBatchIds deletes the storage folder.
      for (const row of rowsToProcess) {
        if (!row._restoredHoverFile && row._restoredHoverUrl) {
          try {
            const hoverRef = ref(storage, row._restoredHoverUrl);
            const hoverBlob = await getBlob(hoverRef);
            let extractedHoverName = `restored_hover_${Date.now()}.jpg`;
            try {
              const decodedUrl = decodeURIComponent(row._restoredHoverUrl.split('?')[0]);
              const lastSegment = decodedUrl.substring(decodedUrl.lastIndexOf('/') + 1);
              if (lastSegment) {
                extractedHoverName = lastSegment.replace(/^(?:\d+_|hover_\d+_)/, '');
              }
            } catch (err) {}
            const file = new File([hoverBlob], extractedHoverName, {
              type: hoverBlob.type || 'image/jpeg',
            });
            file.preview = URL.createObjectURL(hoverBlob);
            row._restoredHoverFile = file;
          } catch (e) {}
        }
      }

      // --- PROCESS DEFERRED DELETIONS PRIOR TO WRITE ---
      try {
        if (pendingDocumentDeletions?.length > 0) {
          setters.setProgressMessage('Cleaning up removed documents...');
          await forcePaint();
          for (const url of pendingDocumentDeletions) {
            try {
              if (url && url.includes('firebasestorage')) {
                await deleteObject(ref(storage, url));
              }
            } catch (err) {
              // Silently ignore 404s if GoogleMap.js already deleted it
              if (err.code !== 'storage/object-not-found') {
                console.warn('Failed to delete pending document:', url, err);
              }
            }
          }
        }

        if (pendingDuplicateDeletions?.length > 0) {
          setters.setProgressMessage('Removing duplicates...');
          await forcePaint();

          for (const m of pendingDuplicateDeletions) {
            const mgId = m.markerGroupID;
            const markerID = m.id;
            const polygonID = m.polygonID;
            const polylineID = m.polylineID;
            const hoverImage = m.hoverImage;
            const photos = m.photos;
            const importBatchId = m.importBatchId || m.data?.importBatchId;

            if (mgId) await deleteDoc(doc(db, 'markerGroups', mgId));
            if (markerID) await deleteDoc(doc(db, 'markers', markerID));
            if (polygonID) await deleteDoc(doc(db, 'polygons', polygonID));
            if (polylineID) await deleteDoc(doc(db, 'polylines', polylineID));

            const deleteStorageRef = async (url) => {
              if (!url || !url.includes('firebasestorage')) return;
              try {
                await deleteObject(ref(storage, url));
              } catch (err) {
                if (err.code !== 'storage/object-not-found') {
                  console.warn('Failed to delete duplicate storage ref:', url, err);
                }
              }
            };

            if (hoverImage) await deleteStorageRef(hoverImage);
            if (photos && Array.isArray(photos)) {
              for (const p of photos) {
                if (p.url) await deleteStorageRef(p.url);
              }
            }

            const documents = m.documents || m.data?.documents;
            if (documents && Array.isArray(documents)) {
              for (const d of documents) {
                if (d.url) await deleteStorageRef(d.url);
              }
            }

            if (importBatchId) {
              try {
                const batchRef = doc(db, 'excelImports', importBatchId);
                const batchSnap = await getDoc(batchRef);
                if (batchSnap.exists()) {
                  const currentCount = batchSnap.data().markerCount || 0;
                  if (currentCount <= 1) {
                    await deleteDoc(batchRef);
                  } else {
                    await updateDoc(batchRef, { markerCount: currentCount - 1 });
                  }
                }
              } catch (err) {}
            }
          }
        }

        if (pendingReinstateBatchIds?.length > 0) {
          setters.setProgressMessage('Cleaning up reinstated batches...');
          await forcePaint();

          for (const batchId of pendingReinstateBatchIds) {
            const groupsQuery = query(
              collection(db, 'markerGroups'),
              where('importBatchId', '==', batchId),
            );
            const groupsSnapshot = await getDocs(groupsQuery);
            const markersQuery = query(
              collection(db, 'markers'),
              where('importBatchId', '==', batchId),
            );
            const markersSnapshot = await getDocs(markersQuery);

            const storageDeletePromises = [];
            groupsSnapshot.docs.forEach((docSnap) => {
              const groupId = docSnap.id;
              const matchingRow = rowsToProcess.find(
                (r) => r.originalMarkerGroupID === groupId,
              );
              const keepHoverStorage =
                matchingRow &&
                matchingRow._restoredHoverUrl &&
                !matchingRow._restoredHoverFile;

              if (!keepHoverStorage) {
                storageDeletePromises.push(
                  listAll(ref(storage, `marker_images/${groupId}`))
                    .then((res) =>
                      Promise.all(
                        res.items.map((item) => deleteObject(item).catch(() => {})),
                      ),
                    )
                    .catch(() => {}),
                );
              }
              storageDeletePromises.push(
                listAll(ref(storage, `photos/${groupId}`))
                  .then((res) =>
                    Promise.all(
                      res.items.map((item) => deleteObject(item).catch(() => {})),
                    ),
                  )
                  .catch(() => {}),
              );
              storageDeletePromises.push(
                listAll(ref(storage, `documents/${groupId}`))
                  .then((res) =>
                    Promise.all(
                      res.items.map((item) => deleteObject(item).catch(() => {})),
                    ),
                  )
                  .catch(() => {}),
              );
            });
            if (storageDeletePromises.length > 0)
              await Promise.all(storageDeletePromises);

            const allDocsToDelete = [...groupsSnapshot.docs, ...markersSnapshot.docs];
            for (let i = 0; i < allDocsToDelete.length; i += 400) {
              const chunk = allDocsToDelete.slice(i, i + 400);
              const deleteBatch = writeBatch(db);
              chunk.forEach((d) => deleteBatch.delete(d.ref));
              await deleteBatch.commit();
              await yieldToMain();
            }
            await deleteDoc(doc(db, 'excelImports', batchId));
          }
        }
      } catch (err) {
        console.error('Failed to process deferred deletions', err);
        throw new Error('Failed to process deferred deletions: ' + err.message);
      } finally {
        setters.setPendingDocumentDeletions([]);
        setters.setPendingDuplicateDeletions([]);
        setters.setPendingReinstateBatchIds([]);
      }

      const batch = writeBatch(db);
      const importBatchId = crypto.randomUUID
        ? crypto.randomUUID()
        : `batch_${Date.now()}`;

      setters.setProgressMessage('');
      setters.setUploadProgress({ current: 0, total: rowsToProcess.length });
      await forcePaint();

      const processedLocalIds = [];

      for (let rIndex = 0; rIndex < rowsToProcess.length; rIndex++) {
        if (refs.cancelProcessRef.current) break;

        setters.setUploadProgress({ current: rIndex + 1, total: rowsToProcess.length });
        await forcePaint();

        const row = rowsToProcess[rIndex];
        const markerRef = doc(collection(db, 'markers'));
        const markerGroupRef = doc(collection(db, 'markerGroups'));

        let lat = 0;
        let lng = 0;

        // PRIORITY 1: Explicitly assigned coordinates (e.g. from the geocoder in ExcelDataView)
        const explicitLat =
          row['lat'] ??
          row['Lat'] ??
          row['LAT'] ??
          row['latitude'] ??
          row['Latitude'] ??
          row['LATITUDE'];
        const explicitLng =
          row['lng'] ??
          row['Lng'] ??
          row['LNG'] ??
          row['longitude'] ??
          row['Longitude'] ??
          row['LONGITUDE'];

        if (
          explicitLat !== undefined &&
          explicitLng !== undefined &&
          explicitLat !== '' &&
          explicitLng !== ''
        ) {
          const pLat = parseFloat(String(explicitLat).trim());
          const pLng = parseFloat(String(explicitLng).trim());
          if (!isNaN(pLat) && !isNaN(pLng)) {
            lat = pLat;
            lng = pLng;
          }
        }

        // PRIORITY 2: Mapped string extraction via agnostic regex
        if (lat === 0 && lng === 0) {
          const posString = getMappedOrFallbackValue(
            row,
            'POSITION (LAT, LNG)',
            null,
            'POSITION (LAT, LNG)',
          );
          if (posString) {
            const matches = posString.match(/-?\d+(\.\d+)?/g);
            if (matches && matches.length >= 2) {
              const pLat = parseFloat(matches[0]);
              const pLng = parseFloat(matches[1]);
              if (!isNaN(pLat) && !isNaN(pLng)) {
                lat = pLat;
                lng = pLng;
              }
            }
          }
        }

        if (lat === 0 && lng === 0) {
          continue;
        }

        const iconColKey = Object.keys(rowMappings).find(
          (key) => rowMappings[key] === 'ICON',
        );
        let selectedIconId = getMappedOrFallbackValue(row, 'ICON', null, 'Icon');

        // Resolve string to ID securely
        if (selectedIconId && String(selectedIconId).trim() !== '') {
          const cleanId = String(selectedIconId).trim();
          const matchedIcon = markerIcons?.find(
            (i) =>
              i.id === selectedIconId ||
              (normalizeString(i.name) !== '' &&
                normalizeString(i.name) === normalizeString(cleanId)),
          );
          if (matchedIcon) {
            selectedIconId = matchedIcon.id;
          } else {
            selectedIconId = null;
          }
        } else {
          selectedIconId = null;
        }

        if (!selectedIconId && iconColKey && rowSubMappings[iconColKey]) {
          selectedIconId = rowSubMappings[iconColKey];
        }
        if (!selectedIconId) {
          selectedIconId = markerIcons?.find((icon) => icon.isDefault === true)?.id || '';
        }

        let finalName = getMappedOrFallbackValue(row, 'INFO', 'TITLE', 'Title');
        if (!finalName && row[columns[0]] !== undefined)
          finalName = String(row[columns[0]]);
        finalName = formatText(finalName);

        const rawCategory = getMappedOrFallbackValue(row, 'INFO', 'CATEGORY', 'Category');
        let resolvedCategoryId = null;
        if (rawCategory) {
          const matchedCat = categories.find(
            (c) =>
              c.id === rawCategory ||
              (c.name && normalizeString(c.name) === normalizeString(rawCategory)),
          );
          if (matchedCat) resolvedCategoryId = matchedCat.id;
        }

        const rawRegion = getMappedOrFallbackValue(row, 'INFO', 'REGION', 'Region');
        let resolvedRegionId = null;
        if (rawRegion) {
          const matchedReg = regions.find(
            (r) =>
              r.id === rawRegion ||
              (r.name && normalizeString(r.name) === normalizeString(rawRegion)),
          );
          if (matchedReg) resolvedRegionId = matchedReg.id;
        }

        let finalHoverImageUrl = null;

        if (!refs.cancelProcessRef.current) {
          let rawHoverImage = getMappedOrFallbackValue(
            row,
            'HOVER PHOTO',
            null,
            'Hover Photo',
          );

          const isAutoHoverFallback =
            !rawHoverImage && !row._restoredHoverFile && !row._restoredHoverUrl;
          if (isAutoHoverFallback && row.matchedFiles && row.matchedFiles.length > 0) {
            rawHoverImage = row.matchedFiles[0].name || 'auto_hover.jpg';
          }

          if (rawHoverImage || row._restoredHoverFile || row._restoredHoverUrl) {
            let matchedFile = null;

            if (row._restoredHoverFile) {
              matchedFile = row._restoredHoverFile;
            }
            if (!matchedFile && stagedFiles && stagedFiles.length > 0 && rawHoverImage) {
              matchedFile = getNormalizedFileMatch(stagedFiles, rawHoverImage);
            }
            if (
              !matchedFile &&
              row.matchedFiles &&
              row.matchedFiles.length > 0 &&
              rawHoverImage
            ) {
              matchedFile = getNormalizedFileMatch(row.matchedFiles, rawHoverImage);
            }
            if (
              !matchedFile &&
              isAutoHoverFallback &&
              row.matchedFiles &&
              row.matchedFiles.length > 0
            ) {
              matchedFile = row.matchedFiles[0];
            }

            if (matchedFile) {
              let safeFileName = matchedFile.name ? matchedFile.name : 'image.jpg';
              try {
                let blobToUpload = await ensureValidBlob(matchedFile);

                const isVideo =
                  matchedFile.type?.startsWith('video/') ||
                  (matchedFile.name &&
                    matchedFile.name.match(/\.(mp4|mov|avi|wmv|webm)$/i));

                const isAudio =
                  matchedFile.type?.startsWith('audio/') ||
                  (matchedFile.name && matchedFile.name.match(/\.(mp3|wav|ogg|m4a)$/i));

                if (isAudio) {
                  blobToUpload = new Blob([SOUND_WAVE_ICON_SVG_STRING], {
                    type: 'image/svg+xml',
                  });
                  safeFileName = safeFileName.replace(/\.[^/.]+$/, '') + '.svg';
                } else if (isVideo) {
                  const frameBlob = await new Promise((resolve) => {
                    const video = document.createElement('video');
                    video.preload = 'metadata';
                    const objUrl =
                      matchedFile.preview || URL.createObjectURL(blobToUpload);
                    video.src = objUrl;
                    video.muted = true;
                    video.playsInline = true;
                    video.onloadeddata = () => {
                      video.currentTime = 0.1;
                    };
                    video.onseeked = () => {
                      const canvas = document.createElement('canvas');
                      canvas.width = video.videoWidth || 640;
                      canvas.height = video.videoHeight || 480;
                      const ctx = canvas.getContext('2d');
                      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                      canvas.toBlob(
                        (blob) => {
                          if (!matchedFile.preview) URL.revokeObjectURL(objUrl);
                          resolve(blob);
                        },
                        'image/jpeg',
                        0.8,
                      );
                    };
                    video.onerror = () => {
                      if (!matchedFile.preview) URL.revokeObjectURL(objUrl);
                      resolve(null);
                    };
                  });
                  if (frameBlob) {
                    blobToUpload = frameBlob;
                    safeFileName = safeFileName.replace(/\.[^/.]+$/, '') + '.jpg';
                  }
                }

                if (finalHoverImageUrl) {
                  // Fallback hit, do nothing
                } else if (matchedFile.isScrapedWebPhoto && !isAudio && !isVideo) {
                  const response = await fetch(matchedFile.scrapedUrl);
                  const blob = await response.blob();
                  const imageRef = ref(
                    storage,
                    `marker_images/${markerGroupRef.id}/hover_${Date.now()}_${safeFileName}`,
                  );
                  await uploadBytes(imageRef, blob);
                  finalHoverImageUrl = await getDownloadURL(imageRef);
                } else if (matchedFile.isGooglePhoto && !isAudio && !isVideo) {
                  const functions = getFunctions(getApp(), 'australia-southeast1');
                  const scrapeFunction = httpsCallable(
                    functions,
                    'scrapeAndStoreWebPhoto',
                  );
                  const result = await scrapeFunction({
                    sourceUrl: matchedFile.sourceUrl,
                    fileName: `hover_${Date.now()}_${safeFileName}`,
                    clientUrl: window.location.href,
                  });
                  const response = await fetch(result.data.url);
                  const blob = await response.blob();
                  const imageRef = ref(
                    storage,
                    `marker_images/${markerGroupRef.id}/hover_${Date.now()}_${safeFileName}`,
                  );
                  await uploadBytes(imageRef, blob);
                  finalHoverImageUrl = await getDownloadURL(imageRef);
                } else {
                  const imageRef = ref(
                    storage,
                    `marker_images/${markerGroupRef.id}/hover_${Date.now()}_${safeFileName}`,
                  );
                  await uploadBytes(imageRef, blobToUpload);
                  finalHoverImageUrl = await getDownloadURL(imageRef);
                }
              } catch (err) {
                console.error('Hover image upload error:', err);
                finalHoverImageUrl = row._restoredHoverUrl || rawHoverImage;
              }
            } else if (row._restoredHoverUrl) {
              finalHoverImageUrl = row._restoredHoverUrl;
            } else if (rawHoverImage && rawHoverImage.startsWith('http')) {
              let cleanUrl = rawHoverImage.replace('{size}', '800');
              try {
                const response = await fetch(cleanUrl);
                if (response.ok) {
                  const blob = await response.blob();
                  const imageRef = ref(
                    storage,
                    `marker_images/${markerGroupRef.id}/hover_${Date.now()}.jpg`,
                  );
                  await uploadBytes(imageRef, blob);
                  finalHoverImageUrl = await getDownloadURL(imageRef);
                } else {
                  finalHoverImageUrl = cleanUrl.includes('google.com') ? null : cleanUrl;
                }
              } catch (err) {
                finalHoverImageUrl = cleanUrl.includes('google.com') ? null : cleanUrl;
              }
            } else {
              finalHoverImageUrl = rawHoverImage;
            }
          }
        }

        const extractedDescription = getMappedOrFallbackValue(
          row,
          'DESCRIPTION',
          null,
          'Description',
        );
        const photosArray = [];

        if (row.matchedFiles && row.matchedFiles.length > 0) {
          for (let i = 0; i < row.matchedFiles.length; i++) {
            if (refs.cancelProcessRef.current) break;

            const file = row.matchedFiles[i];
            const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`;
            const safeFileName = file.name ? file.name : 'image.jpg';
            let url = '';

            try {
              let blobToUpload = await ensureValidBlob(file);

              if (url) {
                // Fallback hit, do nothing
              } else if (file.isScrapedWebPhoto) {
                const response = await fetch(file.scrapedUrl);
                const blob = await response.blob();
                const fileRef = ref(
                  storage,
                  `photos/${markerGroupRef.id}/${fileId}_${safeFileName}`,
                );
                await uploadBytes(fileRef, blob);
                url = await getDownloadURL(fileRef);
              } else if (file.isGooglePhoto) {
                const functions = getFunctions(getApp(), 'australia-southeast1');
                const scrapeFunction = httpsCallable(functions, 'scrapeAndStoreWebPhoto');
                const result = await scrapeFunction({
                  sourceUrl: file.sourceUrl,
                  fileName: safeFileName,
                  clientUrl: window.location.href,
                });
                const response = await fetch(result.data.url);
                const blob = await response.blob();
                const fileRef = ref(
                  storage,
                  `photos/${markerGroupRef.id}/${fileId}_${safeFileName}`,
                );
                await uploadBytes(fileRef, blob);
                url = await getDownloadURL(fileRef);
              } else {
                const fileRef = ref(
                  storage,
                  `photos/${markerGroupRef.id}/${fileId}_${safeFileName}`,
                );
                await uploadBytes(fileRef, blobToUpload);
                url = await getDownloadURL(fileRef);
              }

              let fileType = 'image';
              if (file.type && file.type.startsWith('video/')) fileType = 'video';
              else if (file.type && file.type.startsWith('audio/')) fileType = 'audio';
              else if (file.name && file.name.match(/\.(mp4|mov|avi|wmv|webm)$/i))
                fileType = 'video';
              else if (file.name && file.name.match(/\.(mp3|wav|ogg|m4a)$/i))
                fileType = 'audio';

              photosArray.push({
                description: file.description
                  ? String(file.description)
                  : i === 0
                    ? extractedDescription || ''
                    : '',
                fileName: safeFileName,
                id: fileId,
                type: fileType,
                uploadedAt: new Date().toISOString(),
                url: url,
              });
            } catch (uploadErr) {
              console.error('Failed to upload or scrape photo array item:', uploadErr);
            }
          }
        } else if (extractedDescription && !refs.cancelProcessRef.current) {
          photosArray.push({
            description: extractedDescription,
            fileName: '',
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: 'text',
            uploadedAt: new Date().toISOString(),
            url: '',
          });
        }

        const documentsArray = [];
        if (row.documents && row.documents.length > 0) {
          for (let i = 0; i < row.documents.length; i++) {
            if (refs.cancelProcessRef.current) break;
            const file = row.documents[i];

            if (file instanceof File || file.preview || (file.name && !file.url)) {
              const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-doc${i}`;
              const safeFileName = file.name ? file.name : `document_${i}`;
              try {
                let dataToUpload = file;
                let contentType = file.type || 'application/octet-stream';

                if (!(file instanceof Blob)) {
                  if (file.preview && file.preview.startsWith('blob:')) {
                    const res = await fetch(file.preview);
                    dataToUpload = await res.blob();
                  } else {
                    throw new Error('Invalid file object and no preview URL available.');
                  }
                }

                const metadata = { contentType };
                const storagePath = `documents/${markerGroupRef.id}/${fileId}_${safeFileName}`;
                const fileRef = ref(storage, storagePath);

                await uploadBytes(fileRef, dataToUpload, metadata);
                const url = await getDownloadURL(fileRef);

                documentsArray.push({
                  fileName: safeFileName,
                  id: fileId,
                  uploadedAt: new Date().toISOString(),
                  url: url,
                });
              } catch (docErr) {
                console.error(`Failed to upload document: ${safeFileName}`, docErr);
              }
            } else if (file.url) {
              documentsArray.push(file);
            }
          }
        }

        const finalLatNum = Number(lat.toFixed(10));
        const finalLngNum = Number(lng.toFixed(10));

        const markerData = {
          iconID: selectedIconId,
          importBatchId: importBatchId,
          markerGroupID: markerGroupRef.id,
          position: {
            lat: isNaN(finalLatNum) ? 0 : finalLatNum,
            lng: isNaN(finalLngNum) ? 0 : finalLngNum,
          },
        };

        const markerGroupData = {
          date:
            getMappedOrFallbackValue(row, 'INFO', 'DATE', 'Date') ||
            new Date().toISOString().split('T')[0],
          importBatchId: importBatchId,
          lastModified: new Date().toISOString(),
          markerID: markerRef.id,
          title: finalName || '',
          visibility:
            formatText(getMappedOrFallbackValue(row, 'VISIBILITY', null, 'Visibility')) ||
            'PUBLIC',
        };

        if (row.polygonID) markerGroupData.polygonID = row.polygonID;
        if (row.polylineID) markerGroupData.polylineID = row.polylineID;
        if (row._shapeStyleProps && typeof row._shapeStyleProps === 'object') {
          Object.assign(markerGroupData, row._shapeStyleProps);
        }
        if (photosArray.length > 0) markerGroupData.photos = photosArray;
        if (finalHoverImageUrl) markerGroupData.hoverImage = finalHoverImageUrl;
        if (documentsArray.length > 0) markerGroupData.documents = documentsArray;

        const addIfValid = (dbKey, mappedValue, applyFormat = true) => {
          if (mappedValue !== null && mappedValue !== undefined && mappedValue !== '') {
            markerGroupData[dbKey] = applyFormat ? formatText(mappedValue) : mappedValue;
          }
        };

        addIfValid(
          'address',
          getMappedOrFallbackValue(row, 'INFO', 'LOCATION ADDRESS', 'Address'),
          true,
        );
        addIfValid('categoryId', resolvedCategoryId, false);
        addIfValid('city', getMappedOrFallbackValue(row, 'INFO', 'CITY', 'City'), true);
        addIfValid(
          'clientName',
          getMappedOrFallbackValue(row, 'INFO', 'CLIENT NAME', 'Client Name'),
          true,
        );
        addIfValid(
          'controller',
          getMappedOrFallbackValue(row, 'INFO', 'CONTROLLER', 'Controller'),
          true,
        );
        addIfValid(
          'country',
          getMappedOrFallbackValue(row, 'INFO', 'COUNTRY', 'Country'),
          true,
        );
        addIfValid(
          'hoverContent',
          getMappedOrFallbackValue(row, 'HOVER TEXT', null, 'Hover Text'),
          true,
        );
        addIfValid(
          'keywords',
          getMappedOrFallbackValue(row, 'INFO', 'KEYWORDS', 'Keywords'),
          true,
        );
        addIfValid(
          'locationId',
          getMappedOrFallbackValue(row, 'INFO', 'LOCATION ID', 'Location Id'),
          true,
        );
        addIfValid('regionId', resolvedRegionId, false);
        addIfValid(
          'state',
          getMappedOrFallbackValue(row, 'INFO', 'STATE', 'State'),
          true,
        );
        addIfValid(
          'website',
          getMappedOrFallbackValue(row, 'INFO', 'WEB SITE', 'Web Site'),
          false,
        );
        addIfValid(
          'zip',
          getMappedOrFallbackValue(row, 'INFO', 'POSTCODE/ZIP', 'Zip'),
          true,
        );

        const rawShowcase = getMappedOrFallbackValue(row, 'INFO', 'SHOWCASE', 'Showcase');
        if (rawShowcase !== null && rawShowcase !== undefined && rawShowcase !== '') {
          const s = String(rawShowcase).trim().toLowerCase();
          markerGroupData.showcase = s === 'true' || s === 'yes' || s === '1';
        }

        const rawHilight = getMappedOrFallbackValue(row, 'INFO', 'HILIGHT', 'Hilight');
        if (rawHilight !== null && rawHilight !== undefined && rawHilight !== '') {
          const s = String(rawHilight).trim().toLowerCase();
          markerGroupData.hilight = s === 'true' || s === 'yes' || s === '1';
        }

        const internalAndStandardKeys = [
          'id',
          'photoMatch',
          'matchedFiles',
          '_restoredHoverFile',
          '_restoredHoverUrl',
          '_shapeStyleProps',
          '_manuallyAssigned',
          '_localMappings',
          '_hasRichTextFlag',
          'originalMarkerGroupID',
          'polygonID',
          'polylineID',
          'dashColor',
          'isDashed',
          'dashLength',
          'dashGap',
          'strokeColor',
          'strokeWeight',
          'strokeOpacity',
          'fillColor',
          'fillOpacity',
          'lat',
          'lng',
          'latitude',
          'longitude',
          'Lat',
          'Lng',
          'LAT',
          'LNG',
          'LATITUDE',
          'LONGITUDE',
          'POSITION',
          'POSITION (LAT, LNG)',
          'Title',
          'Category',
          'Region',
          'Address',
          'City',
          'State',
          'Zip',
          'Country',
          'Client Name',
          'Controller',
          'Keywords',
          'Web Site',
          'Date',
          'Showcase',
          'Hilight',
          'Visibility',
          'Hover Text',
          'Description',
          'Photos',
          'Hover Photo',
          'Icon',
          'Location Id',
          'color',
          'hoverImage',
          'markerID',
          'markerGroupID',
          'importBatchId',
          'iconID',
          'categoryId',
          'regionId',
          'lastModified',
          'documents',
        ].map((k) => normalizeString(k));

        mappedTemplateCols.forEach((c) =>
          internalAndStandardKeys.push(normalizeString(c)),
        );

        Object.keys(row).forEach((key) => {
          const normKey = normalizeString(key);
          if (!internalAndStandardKeys.includes(normKey)) {
            if (row[key] !== null && row[key] !== undefined && row[key] !== '') {
              markerGroupData[key] =
                typeof row[key] === 'string' ? formatText(row[key]) : row[key];
            }
          }
        });

        batch.set(markerRef, markerData);
        batch.set(markerGroupRef, markerGroupData);

        if (row.polygonID) {
          batch.set(
            doc(db, 'polygons', row.polygonID),
            {
              markerGroupID: markerGroupRef.id,
              markerID: markerRef.id,
              importBatchId: importBatchId,
            },
            { merge: true },
          );
        }
        if (row.polylineID) {
          batch.set(
            doc(db, 'polylines', row.polylineID),
            {
              markerGroupID: markerGroupRef.id,
              markerID: markerRef.id,
              importBatchId: importBatchId,
            },
            { merge: true },
          );
        }

        processedLocalIds.push(row.id);
      }

      setters.setUploadProgress({ current: 0, total: 0 });
      await forcePaint();

      if (processedLocalIds.length === 0) {
        setters.setIsProcessing(false);
        setters.setIsCancelling(false);
        return;
      }

      const importRecord = {
        filename: currentFilename || '-',
        importDate: new Date().toISOString(),
        markerCount: processedLocalIds.length,
        menuId: selectedMenuId || null,
        templateSnapshot: {
          customRows: customRows || [],
          rowMappings: rowMappings || {},
          rowSubMappings: rowSubMappings || {},
          rowStaticValues: rowStaticValues || {},
        },
      };
      batch.set(doc(db, 'excelImports', importBatchId), importRecord);

      await batch.commit();

      setters.setSessionBatches((prev) => ({
        ...prev,
        [importBatchId]: processedLocalIds,
      }));
      setters.setSuccessfullyImportedIds((prev) => {
        const next = [...prev, ...processedLocalIds];

        if (!refs.cancelProcessRef.current) {
          const unimportedCount = parsedData.length - next.length;

          setTimeout(() => {
            if (unimportedCount === 0) {
              setters.setParsedData([]);
              setters.setColumns([]);
              setters.setSelectedRowIds([]);
              if (setters.setCheckedRightIds) setters.setCheckedRightIds([]);
              setters.setStagedFiles([]);
              setters.setSuccessfullyImportedIds([]);
              setters.setSessionBatches({});
              setters.setManualMatchRowId(null);
              setters.setPendingNextColumn(null);
              setters.setResolveCoordsState({
                open: false,
                rowId: null,
                searchTarget: '',
                actualTitle: '',
                geoSuffix: '',
              });
              setters.setInvalidMatchState({
                open: false,
                rowId: null,
                colName: null,
                cellValue: '',
                type: null,
              });
              setters.setAlertState({
                open: true,
                message: `Successfully imported all ${processedLocalIds.length} markers.`,
                severity: 'success',
              });
            } else {
              setters.setConfirmDialog({
                open: true,
                type: 'ALL_IMPORTED',
                message: `Successfully imported ${processedLocalIds.length} markers. There are ${unimportedCount} un-imported rows remaining in the grid. Clear all data anyway?`,
                confirmText: 'Clear Data',
                cancelText: 'Keep Data',
                severity: 'info',
              });
            }
          }, 300);
        }

        return next;
      });

      setters.setSelectedRowIds([]);
      setters.setViewMode('HISTORY_VIEW');
    } catch (error) {
      console.error('Import Batch Error:', error);
      setters.setAlertState({
        open: true,
        message: `Failed to process import: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
      setters.setIsCancelling(false);
      setters.setUploadProgress({ current: 0, total: 0 });
    }
  };

  return { executeSave };
};
